import type { CalendarApi, EventApi, EventInput } from "@fullcalendar/core";
import { EventImpl } from "@fullcalendar/core/internal";
import { areIntervalsOverlapping } from "date-fns/areIntervalsOverlapping";
import { isEqual } from "lodash-es";
import {
  computed,
  Ref,
  ref,
  shallowRef,
  watch,
  WatchStopHandle,
  WritableComputedRef,
} from "vue";

import type {
  Calendar,
  Controller,
  EventId,
  FullEvent,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import { isPassiveEvent } from "@/components/eventInfo";
import { useBrowserStore } from "@/composables/useBrowserStore";
import type { CalendarsRef } from "@/composables/useCalendars";
import type { UseProfile } from "@/composables/useProfile";
import { getDuration } from "@/event";
import { releasableRef } from "@/ref";
import { pause } from "@/utils/pause";

import {
  generateAggregatedAllDayEvents,
  isAggregatedAllDayEvent,
} from "../helpers/aggregated-all-day-event";
import { isStaticGhostEvent } from "./static-ghost-event";

type ExtendedProps = {
  calendarId: string;
  overlappingIndex: number;
  calendar?: Calendar;
  event?: FullEvent;
  /**
   * The live event subscription owned by `useEvents` (one per visible
   * event). `PlannerEventWrapper` renders from it instead of re-fetching;
   * it must NOT release it.
   */
  eventRef?: Readonly<ReleasableRef<FullEvent | null>>;
};
export type ExtendedEventInput = EventInput & {
  extendedProps: ExtendedProps;
};

type FCEvent = EventImpl | ExtendedEventInput | EventApi;

type DateInterval = {
  start: Date;
  end: Date;
};

const dateInterval = (event: FCEvent): DateInterval | null =>
  event.start instanceof Date && event.end instanceof Date
    ? { start: event.start, end: event.end }
    : null;

const isNonEmptyString = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.length > 0;

export const useEvents = (
  isWeekView: Ref<boolean>,
  dates: Readonly<Ref<Date[]>>,
  defaultCalendarId: string | undefined,
  calendarAPI: () => CalendarApi | undefined,
  controller: Controller,
  calendars: CalendarsRef,
  profile: Ref<UseProfile>,
  // `readOnly` builds every event as non-editable: the per-event
  // startEditable/durationEditable flags override FullCalendar's global
  // `editable: false`, so a read-only calendar must clear them at the source.
  options: { topPaneState: string; readOnly?: boolean }
): {
  isAllDaySlotMinimizable: Ref<boolean>;
  isAllDaySlotMinimized: WritableComputedRef<boolean>;
  isLoadingEvents: Ref<boolean>;
  loadEvents: (isReleasingPreviousEvents?: boolean) => void;
  /** Stops the watchers and releases the event id refs and event refs. */
  release: () => void;
  timedEvents: Ref<ExtendedEventInput[]>;
  allDayEvents: Ref<ExtendedEventInput[]>;
} => {
  const isLoadingDayEvents = ref(true);
  const isLoadingAllDayEvents = ref(true);
  const isLoadingEvents = computed(
    () => isLoadingDayEvents.value || isLoadingAllDayEvents.value
  );
  let dayEventIds: Readonly<ReleasableRef<string[] | null>> =
    releasableRef(null);
  let allDayEventIds: Readonly<ReleasableRef<string[] | null>> =
    releasableRef(null);
  let unwatchDates: WatchStopHandle = () => {};
  const watchDates = () => {
    unwatchDates();
    unwatchDates = watch(
      dates,
      async (dates, oldDates) => {
        if (
          dates.length !== oldDates?.length ||
          !isEqual(dates[0], oldDates?.[0])
        ) {
          // NB: we intentionally do *not* clear the existing events here.
          // Removing every source and re-adding asynchronously makes events
          // that are visible in both the old and new ranges blink on each
          // layout/period change. Instead the id watchers below reconcile the
          // calendar: stale events (outside the new range) are removed and only
          // genuinely new events are added, so overlapping events stay put.
          dayEventIds.release();
          if (dates.length > 0) {
            isLoadingDayEvents.value = true;
            dayEventIds = await controller.event.days(
              dates,
              "without-all-day",
              true
            );
            isLoadingAllDayEvents.value = true;
            allDayEventIds = await controller.event.days(
              dates,
              "only-all-day",
              true
            );
            watchEventIds();
          }
        }
      },
      { immediate: true, deep: true }
    );
  };
  // Live event subscriptions of the visible range, keyed by event id.
  // Acquired once per id (reused across rebuilds and range overlaps) and
  // shared with the PlannerEventWrapper components through extendedProps,
  // so each visible event costs a single `event.get`. Owned here: pruned
  // when an id leaves the range, released wholesale on destroy.
  const eventRefs = new Map<
    string,
    Readonly<ReleasableRef<FullEvent | null>>
  >();
  // Bumped by destroy() so acquisitions still suspended in `event.get`
  // release their result instead of repopulating the cleared map.
  let eventRefsEpoch = 0;
  const acquireEventRef = async (
    id: string
  ): Promise<Readonly<ReleasableRef<FullEvent | null>>> => {
    const existing = eventRefs.get(id);
    if (existing) return existing;
    const epoch = eventRefsEpoch;
    const event = await controller.event.get(id);
    // A concurrent acquisition landed first, or destroy() ran meanwhile:
    // this subscription has no owner slot, release it.
    const raced = eventRefs.get(id);
    if (raced) {
      event.release();
      return raced;
    }
    if (epoch !== eventRefsEpoch) {
      event.release();
      return event;
    }
    eventRefs.set(id, event);
    return event;
  };
  const pruneEventRefs = () => {
    const liveIds = new Set([
      ...(dayEventIds.value ?? []),
      ...(allDayEventIds.value ?? []),
    ]);
    for (const [id, ref] of eventRefs) {
      if (!liveIds.has(id)) {
        ref.release();
        eventRefs.delete(id);
      }
    }
  };
  const destroy = () => {
    isLoadingDayEvents.value = false;
    isLoadingAllDayEvents.value = false;
    pause(unwatchDates);
    pause(unwatchEventIds);
    pause(() => {
      calendarAPI()?.removeAllEventSources();
      dayEventIds.release();
      allDayEventIds.release();
      dayEventIds = releasableRef(null);
      allDayEventIds = releasableRef(null);
      unwatchDates = () => {};
      watchers.value = [];
      eventRefsEpoch++;
      for (const ref of eventRefs.values()) ref.release();
      eventRefs.clear();
    });
  };
  const buildEvents = async (ids: string[]): Promise<ExtendedEventInput[]> => {
    const built = await Promise.all(
      ids.map(async (id) => {
        const event = await acquireEventRef(id);
        if (event.value) {
          const calendar = calendars.value?.byId.get(event.value.calendar);
          if (calendar)
            return buildEventInput(event.value, calendar, {
              readOnly: options.readOnly,
              eventRef: event,
            });
        }
        return undefined;
      })
    );
    return built.filter((event) => event !== undefined);
  };
  const watchers = ref<WatchStopHandle[]>([]);
  const unwatchEventIds = () => watchers.value.map((unwatch) => unwatch());
  // Shallow: the inputs are replaced wholesale, and a deep ref would proxy
  // the nested `eventRef` and auto-unwrap it on access.
  const timedEvents = shallowRef<ExtendedEventInput[]>([]);
  const allDayEvents = shallowRef<ExtendedEventInput[]>([]);
  const watchEventIds = () => {
    unwatchEventIds();
    watchers.value = [
      watch(
        dayEventIds,
        async (ids) => {
          isLoadingDayEvents.value = true;
          pruneEventRefs();
          const api = calendarAPI();
          if (!ids || ids.length === 0) {
            removeStaleTimedEvents(api, new Set());
            timedEvents.value = [];
            isLoadingDayEvents.value = false;
            return;
          }
          if (!api || !ids) return;
          timedEvents.value = await buildEvents(ids);
          // Reconcile: drop timed events that are no longer in range, then add
          // only the missing ones. This keeps events that remain visible across
          // the change mounted (no blink) instead of clearing + reloading all.
          const desiredIds = new Set(
            timedEvents.value.map(({ id }) => id).filter(isNonEmptyString)
          );
          removeStaleTimedEvents(api, desiredIds);
          const existingIds = api
            .getEvents()
            .filter(({ allDay }) => !allDay)
            .map(({ id }) => id);
          const newEvents = timedEvents.value.filter(
            (e) => e.id && !existingIds.includes(e.id)
          );
          await addEvents(
            isWeekView.value ? handleSimultaneousEvents(newEvents) : newEvents,
            api
          );
          isLoadingDayEvents.value = false;
        },
        { immediate: true }
      ),
      watch(
        allDayEventIds,
        async (ids) => {
          isLoadingAllDayEvents.value = true;
          isAllDaySlotMinimizable.value = false;
          pruneEventRefs();
          const api = calendarAPI();
          if (!api || !ids) return;
          allDayEvents.value = await buildEvents(ids);
          // Drop all-day events that fell out of range so they don't linger
          // when the range shrinks (the previous removeAllEventSources used to
          // handle this). Aggregated all-day events are managed by
          // buildAllDayEvents below, so we leave them to it.
          const desiredAllDayIds = new Set(
            allDayEvents.value.map(({ id }) => id).filter(isNonEmptyString)
          );
          removeStaleAllDayEvents(api, desiredAllDayIds);
          if (isWeekView.value) {
            buildAllDayEvents(allDayEvents.value, api);
          } else {
            await addEvents(allDayEvents.value, api);
          }
          isLoadingAllDayEvents.value = false;
        },
        { immediate: true }
      ),
    ];
  };
  const deleteEvent = (id: EventId, calendarAPI: CalendarApi) =>
    pause(() => {
      calendarAPI.getEventById(id)?.remove();
    });
  // Remove timed events that are no longer part of the visible range, leaving
  // events still in range (and the static ghost/drag event) untouched so they
  // don't blink when the layout or period changes.
  const removeStaleTimedEvents = (
    api: CalendarApi | undefined,
    desiredIds: Set<string>
  ) =>
    pause(() => {
      if (!api) return;
      api
        .getEvents()
        .filter(
          (e) =>
            !e.allDay &&
            !!e.id &&
            !isStaticGhostEvent(e.id) &&
            !desiredIds.has(e.id)
        )
        .forEach((e) => e.remove());
    });
  // Same as above for the individual all-day events. Aggregated all-day events
  // (synthetic ids) are owned by buildAllDayEvents, so we never touch them here.
  const removeStaleAllDayEvents = (
    api: CalendarApi | undefined,
    desiredIds: Set<string>
  ) =>
    pause(() => {
      if (!api) return;
      api
        .getEvents()
        .filter(
          (e) =>
            e.allDay &&
            !!e.id &&
            !isStaticGhostEvent(e.id) &&
            !isAggregatedAllDayEvent(e.id) &&
            !desiredIds.has(e.id)
        )
        .forEach((e) => e.remove());
    });
  const addEvents = (events: ExtendedEventInput[], calendarAPI: CalendarApi) =>
    pause(() => {
      events = events.filter(
        (event) => event.id !== calendarAPI.getEventById(event.id!)?.id
      );
      if (events.length > 0)
        calendarAPI.addEventSource({
          events: events,
        });
    });
  const buildAllDayEvents = (
    events: ExtendedEventInput[],
    calendarAPI: CalendarApi
  ) => {
    if (defaultCalendarId) {
      if (events.length === 0) return;
      const { aggregatedAllDayEvents, isSingleRowOfAllDayEvents } =
        generateAggregatedAllDayEvents(dates.value, events, defaultCalendarId);
      isAllDaySlotMinimizable.value = !isSingleRowOfAllDayEvents;
      const existingIds = calendarAPI
        .getEvents()
        .filter(({ allDay }) => allDay)
        .map(({ id }) => id);
      if (isAllDaySlotMinimized.value && isAllDaySlotMinimizable.value) {
        existingIds.forEach((id) => deleteEvent(id, calendarAPI));
        if (!isSingleRowOfAllDayEvents)
          addEvents(aggregatedAllDayEvents, calendarAPI);
        else {
          addEvents(events, calendarAPI);
        }
      } else {
        existingIds.forEach((id) => {
          if (isAggregatedAllDayEvent(id)) deleteEvent(id, calendarAPI);
        });
        const newEvents = events.filter(({ id }) => !existingIds.includes(id!));
        addEvents(newEvents, calendarAPI);
      }
    }
  };
  const {
    isAllDaySlotMinimized,
    isAllDaySlotMinimizable,
    release: releaseAllDaySlot,
  } = useAllDaySlot(profile, { topPaneState: options.topPaneState });
  const stopAllDaySlotWatcher = watch(isAllDaySlotMinimized, async () => {
    isLoadingAllDayEvents.value = true;
    isAllDaySlotMinimizable.value = false;
    const api = calendarAPI();
    const ids = allDayEventIds.value;
    if (!api || !ids) return;
    const events = await buildEvents(ids);
    buildAllDayEvents(events, api);
    isLoadingAllDayEvents.value = false;
  });
  const loadEvents = (isReleasingPreviousEvents = false) => {
    if (isReleasingPreviousEvents) destroy();
    pause(watchDates);
  };
  return {
    isAllDaySlotMinimizable,
    isAllDaySlotMinimized,
    isLoadingEvents,
    loadEvents,
    // `destroy` alone is also used to reload events (see `loadEvents`); the
    // all-day-slot persistence only stops on the final release.
    release: () => {
      stopAllDaySlotWatcher();
      releaseAllDaySlot();
      destroy();
    },
    timedEvents,
    allDayEvents,
  };
};

type AllDaySlotState = "collapsed" | "expanded";

const useAllDaySlot = (
  profile: Ref<UseProfile>,
  storageKeys: { topPaneState: string }
) => {
  const isAllDaySlotMinimizable = ref(false);

  // Load & sync all day slot state with the browser's local storage. The key is
  // route-scoped (built by `usePlanner`) so each planner instance keeps
  // its own top-pane state.
  const { syncState } = useBrowserStore(profile.value.user.accountId);
  const state = syncState<AllDaySlotState>(
    storageKeys.topPaneState,
    "expanded"
  );

  const isAllDaySlotMinimized = computed({
    get() {
      return state.value === "collapsed";
    },
    set(bool: boolean) {
      state.value = bool ? "collapsed" : "expanded";
    },
  });
  return {
    isAllDaySlotMinimized,
    isAllDaySlotMinimizable,
    release: state.release,
  };
};
export const buildEventInput = (
  event: FullEvent,
  calendar?: Calendar,
  options?: {
    readOnly?: boolean;
    eventRef?: Readonly<ReleasableRef<FullEvent | null>>;
  }
): ExtendedEventInput => {
  return {
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    allDay: event.allDay,
    startEditable: !options?.readOnly && event.movable,
    durationEditable: !options?.readOnly && event.editable,
    extendedProps: {
      calendarId: event.calendar,
      overlappingIndex: 0,
      calendar,
      event,
      eventRef: options?.eventRef,
    },
  };
};
const getOverlappingEvents = (event: FCEvent, events: FCEvent[]): FCEvent[] => {
  const interval = dateInterval(event);
  if (!interval) return [];
  return events.filter((eventToCompareWith) => {
    if (!eventToCompareWith.id || eventToCompareWith.allDay) return;
    const comparedInterval = dateInterval(eventToCompareWith);
    return (
      comparedInterval !== null &&
      areIntervalsOverlapping(interval, comparedInterval)
    );
  });
};
const handleSimultaneousEvents = (
  events: ExtendedEventInput[]
): ExtendedEventInput[] => {
  return events.map((event) => {
    if (event.allDay) return event;
    const overlappingEvents = getOverlappingEvents(event, events);
    let overlappingIndex = 0;
    if (overlappingEvents.length > 0) {
      // Reorder indexes
      overlappingIndex = overlappingEvents
        .sort(byAttendance)
        .sort(byStartingTimeAndDuration)
        .findIndex((e) => e.id === event.id);
    }
    return {
      ...event,
      extendedProps: {
        ...event.extendedProps,
        overlappingIndex,
      },
    };
  });
};
export const patchSimultaneousEvents = (
  event: FCEvent,
  events: FCEvent[],
  calendarAPI: CalendarApi,
  isEventRemoved = false
): void => {
  const overlappingEvents = getOverlappingEvents(event, events);
  if (overlappingEvents.length >= 1) {
    if (!isEventRemoved) overlappingEvents.push(event);
    overlappingEvents.forEach(async ({ id }) => {
      if (id && !isStaticGhostEvent(id)) {
        const event = calendarAPI.getEventById(id);
        if (event) {
          const newIndex = overlappingEvents
            .sort(byAttendance)
            .sort(byStartingTimeAndDuration)
            .findIndex((e) => e.id === event.id);
          if (newIndex !== event.extendedProps.overlappingIndex)
            event.setExtendedProp("overlappingIndex", newIndex);
        }
      }
    });
  }
};
const byAttendance = (a: FCEvent, b: FCEvent) => {
  if (!a.id || !b.id) return 0;
  const aCalendar = a.extendedProps.calendar;
  const bCalendar = b.extendedProps.calendar;
  const aEvent = a.extendedProps.event;
  const bEvent = b.extendedProps.event;
  if (!aEvent || !bEvent) return -1;
  const isAPassive = isPassiveEvent(aCalendar, aEvent);
  const isBPassive = isPassiveEvent(bCalendar, bEvent);
  if (isAPassive === isBPassive) return 0;
  if (isAPassive) return 1;
  else return -1;
};
const byStartingTimeAndDuration = (a: FCEvent, b: FCEvent) => {
  if (!a.id || !b.id) return 0;
  const aInterval = dateInterval(a);
  const bInterval = dateInterval(b);
  if (!aInterval || !bInterval) return 0;
  const aDuration = getDuration(aInterval.start, aInterval.end);
  const bDuration = getDuration(bInterval.start, bInterval.end);
  if (aInterval.start.toString() === bInterval.start.toString()) {
    if (aDuration === bDuration) return 0;
    else if (aDuration > bDuration) return -1;
    else return 1;
  } else if (aInterval.start > bInterval.start) return 1;
  else return -1;
};
